import dayjs from 'dayjs';

import type { ByShopRow, CurrencySummary, DashboardCurrencyRow, RevenueShopRow, RevenueTotalRow, SyncFreshness } from '../../api/index.ts';

/** Sorted, deduped currency codes across any number of code lists -- the
 *  page unions every loaded response so the currency select (E2.1 spec 4:
 *  "rendered ONLY when >1 currency present in the data") reflects everything
 *  fetched, not just one endpoint. */
export function collectCurrencies(...codeLists: Array<Array<string | null | undefined>>): string[] {
  const set = new Set<string>();
  for (const list of codeLists) {
    for (const code of list) {
      if (code) set.add(code);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** A zero-activity row for a currency the caller wants to display figures
 *  for but which has no matching entry in the served array (E2.1 spec 4.6:
 *  "zero-activity shops included with 0.00 figures"). */
export function zeroDashboardRow(currencyCode: string): DashboardCurrencyRow {
  return {
    currency_code: currencyCode,
    gross_sales_minor: 0,
    payout_minor: 0,
    gross_profit_minor: 0,
    tax_total_minor: 0,
    profit_minor: 0,
    payout_bp: null,
  };
}

export function zeroCurrencySummary(currencyCode: string): CurrencySummary {
  return {
    currency_code: currencyCode,
    gross_sales_minor: 0,
    payout_minor: 0,
    levy_minor: 0,
    gross_profit_minor: 0,
    tax_total_minor: 0,
    tax_splits: [],
    net_sales_minor: 0,
    profit_minor: 0,
    payout_bp: null,
  };
}

export function pickDashboardRow(rows: DashboardCurrencyRow[], currencyCode: string): DashboardCurrencyRow {
  return rows.find((r) => r.currency_code === currencyCode) ?? zeroDashboardRow(currencyCode);
}

export function pickCurrencySummary(rows: CurrencySummary[], currencyCode: string): CurrencySummary {
  return rows.find((r) => r.currency_code === currencyCode) ?? zeroCurrencySummary(currencyCode);
}

export type DeltaMetricKey = 'gross_sales' | 'payout' | 'payout_pct' | 'gross_profit' | 'tax' | 'net_profit' | 'revenue';

/** Metrics where a DROP is the favorable direction (green) -- payout and
 *  payout % only (owner ruling, section 6: "APPROVED"). Every other metric
 *  (including tax) defaults to the general rule stated in spec 4.2: up is
 *  favorable for gross sales/gross profit/net profit; tax is not named
 *  either way in the spec text, so it follows that same default here. */
const INVERTED_METRICS: ReadonlySet<DeltaMetricKey> = new Set(['payout', 'payout_pct']);

export interface DeltaResult {
  /** Signed percent-of-previous, e.g. 4.1 or -1.2. */
  pct: number;
  direction: 'up' | 'down';
  /** True when this movement is good news for the owner (renders green). */
  favorable: boolean;
}

/** Percent-of-previous delta from two served integers (display-only
 *  derivation, never rendered as money -- spec 4.2). `direction` is derived
 *  from the ACTUAL signed change (current > previous => 'up'), never from
 *  the sign of `pct` -- with a negative `previous` (e.g. a loss), the sign
 *  of the percentage does not track the sign of the change (previous=-500,
 *  current=+300 is a big improvement, but (300 - -500) / -500 is negative),
 *  so deriving direction from pct's sign flips a favorable move to
 *  unfavorable. `pct`'s own denominator is Math.abs(previous) so the
 *  percentage's sign always means increase/decrease in magnitude terms,
 *  independent of direction. Returns null when there is no previous figure
 *  to compare against (no previous-period data, or a previous value of 0
 *  that would make the percentage undefined) or the values are unchanged
 *  (0% delta renders no chip either -- nothing moved). */
export function computeDelta(metric: DeltaMetricKey, current: number, previous: number | null): DeltaResult | null {
  if (previous === null || previous === 0) return null;
  if (current === previous) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const direction: 'up' | 'down' = current > previous ? 'up' : 'down';
  const inverted = INVERTED_METRICS.has(metric);
  const favorable = inverted ? direction === 'down' : direction === 'up';
  return { pct, direction, favorable };
}

export interface RankedShopRow {
  rank: number;
  shop_id: number;
  shop_code: string;
  shop_name: string;
  shop_type: string;
  last_sync_at: string | null;
  sync_freshness: SyncFreshness;
  row: DashboardCurrencyRow;
}

/** Sorts ALL served shops by gross sales desc (for the selected currency) and
 *  assigns the rank column from that order -- zero-activity shops keep their
 *  0.00 row (E2.1 spec 4.6), they just sort to the bottom. */
export function rankShopsByGross(shops: ByShopRow[], currencyCode: string): RankedShopRow[] {
  const withRows = shops.map((shop) => ({ shop, row: pickDashboardRow(shop.by_currency, currencyCode) }));
  withRows.sort((a, b) => b.row.gross_sales_minor - a.row.gross_sales_minor);
  return withRows.map(({ shop, row }, index) => ({
    rank: index + 1,
    shop_id: shop.shop_id,
    shop_code: shop.shop_code,
    shop_name: shop.shop_name,
    shop_type: shop.shop_type,
    last_sync_at: shop.last_sync_at,
    sync_freshness: shop.sync_freshness,
    row,
  }));
}

export interface SyncBadgeMeta {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
}

const SYNC_BADGE: Record<SyncFreshness, SyncBadgeMeta> = {
  fresh: { label: 'Synced', color: 'success' },
  stale: { label: 'Stale', color: 'warning' },
  offline: { label: 'Offline', color: 'error' },
  never: { label: 'Never synced', color: 'default' },
};

export function syncBadgeMeta(freshness: SyncFreshness): SyncBadgeMeta {
  return SYNC_BADGE[freshness];
}

/** last_sync_at rendered in the VIEWER's local time (E2.1 spec 4.6 tooltip
 *  rule) -- distinct from format.ts's UTC-rendered submission dates, which
 *  are calendar dates rather than moments. */
export function formatLocalDateTime(iso: string): string {
  return dayjs(iso).format('YYYY-MM-DD HH:mm');
}

/** Every COMPUTED ratio renders to 2 decimals, v1's precision (owner
 *  field-check 2026-08-12). Configured rates (bpRateText) are not measurements
 *  and keep their trimmed form. */
const RATIO_DECIMALS = 2;

/** "% Gross Profit" from the owner's spreadsheet -- a display-only
 *  derivation from two served integers, never itself sent anywhere. '-' when
 *  gross sales is 0 (the denominator, so the percent is undefined). */
export function grossProfitPercentText(grossProfitMinor: number, grossSalesMinor: number): string {
  if (grossSalesMinor <= 0) return '-';
  return `${((grossProfitMinor / grossSalesMinor) * 100).toFixed(RATIO_DECIMALS)}% of sales`;
}

/**
 * v1's payout ratio (owner ruling 2026-08-12, v1 parity): payout over sales
 * NET OF THE STORED TAX, never over gross and never against a recomputed rate
 * -- the tax figure always comes from the same wire row as the other two.
 * Null when the net-of-tax denominator is 0 or negative (nothing to divide
 * by), which every caller renders as an omitted percentage.
 */
export function payoutRatioPct(payoutMinor: number, grossSalesMinor: number, taxMinor: number): number | null {
  const netOfTax = grossSalesMinor - taxMinor;
  if (netOfTax <= 0) return null;
  return (payoutMinor / netOfTax) * 100;
}

/** A configured rate in basis points as v1 says it: 500 -> "5%",
 *  1500 -> "15%", 250 -> "2.5%". Never a hardcoded rate -- bp comes from the wire. */
export function bpRateText(bp: number): string {
  return `${Number((bp / 100).toFixed(2))}%`;
}

/** Bare column form: "76.0%", or '-' when undecidable. */
export function payoutRatioText(payoutMinor: number, grossSalesMinor: number, taxMinor: number): string {
  const pct = payoutRatioPct(payoutMinor, grossSalesMinor, taxMinor);
  return pct === null ? '-' : `${pct.toFixed(RATIO_DECIMALS)}%`;
}

/** Caption form, truthful about its base: "76.0% of net sales"; undefined
 *  when undecidable so the caller renders no caption at all. */
export function payoutRatioCaption(
  payoutMinor: number,
  grossSalesMinor: number,
  taxMinor: number,
): string | undefined {
  const pct = payoutRatioPct(payoutMinor, grossSalesMinor, taxMinor);
  return pct === null ? undefined : `${pct.toFixed(RATIO_DECIMALS)}% of net sales`;
}

/** The currency the dashboard actually renders: the user's pick while it is
 *  still present in the loaded data, else the first available, else none. */
export function effectiveCurrency(selected: string | null, currencies: readonly string[]): string | null {
  if (selected !== null && currencies.includes(selected)) return selected;
  return currencies[0] ?? null;
}

/** A zero-activity total for a currency with no matching totals-array entry
 *  (mirrors pickCurrencySummary/pickDashboardRow's zero-row fallback). */
export function zeroRevenueTotal(currencyCode: string): RevenueTotalRow {
  return { currency_code: currencyCode, commission_minor: 0, licence_fee_minor: 0, revenue_minor: 0 };
}

/** Picks the RevenueResult.totals row for the selected currency, or a zero
 *  row when that currency has no bucket (e.g. no revenue activity yet). */
export function pickRevenueTotal(totals: RevenueTotalRow[], currencyCode: string): RevenueTotalRow {
  return totals.find((t) => t.currency_code === currencyCode) ?? zeroRevenueTotal(currencyCode);
}

/** Indexes RevenueResult.by_shop rows by shop_id for the ranked shops
 *  table's Revenue column lookup. */
export function revenueRowsByShopId(rows: RevenueShopRow[]): Map<number, RevenueShopRow> {
  return new Map(rows.map((r) => [r.shop_id, r]));
}

/** A shop's revenue_minor for the selected currency -- 0 when the shop has
 *  no revenue row, or its own currency doesn't match the currently selected
 *  dashboard currency (same "never mix currencies" invariant the rest of
 *  the ranked table already follows via pickDashboardRow). */
export function pickShopRevenueMinor(byShopId: Map<number, RevenueShopRow>, shopId: number, currencyCode: string): number {
  const row = byShopId.get(shopId);
  if (row?.currency_code !== currencyCode) return 0;
  return row.revenue_minor;
}
